import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const DEFAULT_INFRA_PARAS = [
  "Situated in Sonipat, Haryana, St. Andrews World School provides a thoughtfully planned setting where purposeful study, creative expression and active participation enrich the school day.",
  "Our modern infrastructure includes contemporary classrooms, advanced science and technology laboratories and a library for independent discovery. Dedicated music, dance and art spaces encourage creative practice, while the auditorium provides a platform for presentations and performances. A medical room supports everyday student care."
];

const DEFAULT_HOLISTIC_PARAS = [
  "The curriculum comes to life through experience. Experiments, project work and purposeful discussion encourage students to connect concepts with observations, investigate questions and refine their understanding. Presenting findings and explaining their reasoning help children develop clarity of thought and confidence in their own voice.",
  "Swimming, basketball, badminton and team activities develop fitness, coordination and sporting discipline. These experiences also put core values into practice: respecting an opponent, acknowledging a mistake, supporting a teammate and persevering after disappointment. Yoga and mindful reflection offer opportunities to pause, strengthen concentration and cultivate inner calm."
];

const DEFAULT_CARE_PARAS = [
  "Emotional well-being is supported through everyday interactions. Children are encouraged to speak openly about a challenging lesson, a friendship concern or the nerves before a performance. Our educators listen attentively and help them work through these moments, building confidence in their ability to seek support and take the next step.",
  "Planning a project, honouring commitments and finding solutions with others give students practical preparation for responsibilities beyond school. Through these everyday experiences, they develop the initiative, adaptability and judgement to navigate future study, work and life."
];

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const renderParagraphs = (raw, fallbackArray = []) => {
  if (!raw) {
    return fallbackArray.map((p, i) => <p key={i}>{p}</p>);
  }
  const parts = String(raw).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  if (!parts.length) {
    return fallbackArray.map((p, i) => <p key={i}>{p}</p>);
  }
  return parts.map((p, i) => <p key={i}>{p}</p>);
};

const parsePipeList = (raw, fallbackArray = []) => {
  if (!raw) return fallbackArray;
  const items = String(raw).split('|').map(s => s.trim()).filter(Boolean);
  return items.length ? items : fallbackArray;
};

const About = () => {
  const pageData = usePage('about-us');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || "About Sonipat Campus";
  const bannerSubtitle = banner.subtitle || "Nurturing Potential and Cultivating Tomorrow's Leaders";
  const bannerBreadcrumb = banner.breadcrumb || "ABOUT US";
  const bannerImgSrc = resolveImage(pageData.banner_image || pageData.bannerImage, '/images/page-banners/about-us.jpg');

  const infra = sections.infrastructure || {};
  const infraKicker = infra.kicker || "Our Infrastructure";
  const infraHeading = infra.heading || "A thoughtfully planned setting";
  const infraBadges = parsePipeList(
    infra.badges,
    ["Modern Classrooms", "Advanced Science & Computer Labs", "Multi-Sport Complex", "Dedicated Creative Studios"]
  );

  const holistic = sections.holistic || {};
  const holisticKicker = holistic.kicker || "Holistic Approach";
  const holisticHeading = holistic.heading || "Learning through experience";
  const holisticHighlights = parsePipeList(
    holistic.highlights,
    ["Inquiry-Driven Experiments", "Team Sports & Fitness", "Yoga & Mindful Reflection", "Creative Arts & Performance"]
  );

  const care = sections.care || {};
  const careKicker = care.kicker || "Student Care";
  const careHeading = care.heading || "Emotional well-being & practical preparation";
  const careQuote = care.quote || "Emotional security is inseparable from educational excellence.";

  const pillars = sections.pillars || {};
  const pillarsHeading = pillars.heading || "Why Parents Choose St. Andrews Sonipat";
  const pillarsSubtitle = pillars.subtitle || "A balanced education that builds intellect, empathy and resilience.";
  const defaultPillars = [
    {
      title: "Academic Rigour & Inquiry",
      desc: "Student-centric CBSE curriculum cultivating conceptual mastery and fearless curiosity.",
      icon: "fa-graduation-cap",
    },
    {
      title: "Athletic & Sports Excellence",
      desc: "Swimming, basketball, cricket, badminton, and martial arts fostering grit and teamwork.",
      icon: "fa-medal",
    },
    {
      title: "Creative Arts & Innovation",
      desc: "Music, theatre, dance, coding and digital learning expanding imaginative horizons.",
      icon: "fa-palette",
    },
    {
      title: "Character & Mindful Well-being",
      desc: "Mentorship and open dialogues ensuring strong emotional foundations for lifelong leadership.",
      icon: "fa-heart-pulse",
    },
  ];
  const pillarItems = (pillars.items && pillars.items.length) ? pillars.items : defaultPillars;

  return (
    <>
      <SEO
        title={pageData.title ? `${pageData.title} | St. Andrews World School` : "About Sonipat Campus | St. Andrews World School"}
        description="Situated in Sonipat, Haryana, St. Andrews World School provides a thoughtfully planned setting where purposeful study, creative expression and active participation enrich the school day."
        keywords="About Sonipat Campus, St Andrews World School Sonipat, SAWS Sonipat, Best School in Sonipat, CBSE School"
      />

      {/* ======================================================== */}
      {/* TOP HERO BANNER: Matching reference design with bottom text */}
      {/* ======================================================== */}
      <section className="hero-banner relative w-full h-[200px] sm:h-[280px] md:h-[360px] lg:h-[440px] overflow-hidden bg-[#0c1a30]">
        <img
          src={bannerImgSrc}
          alt={bannerTitle}
          className="w-full h-full object-fill object-center"
          loading="eager"
        />

        {/* Dark Vignette / Gradient Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25"
          aria-hidden="true"
        />

        {/* Bottom-Centered Text Container */}
        <div className="absolute inset-0 flex flex-col justify-end items-center text-center pb-4 sm:pb-7 md:pb-9 lg:pb-11 px-4 z-10">
          <Reveal className="w-full max-w-4xl mx-auto flex flex-col items-center text-center">
            {/* Main Title in Serif */}
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-[54px] font-serif font-normal md:font-medium text-white tracking-normal drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] leading-tight text-center">
              {bannerTitle}
            </h1>

            {/* Subtitle / Tagline */}
            {bannerSubtitle && (
              <p className="mt-1 sm:mt-2 md:mt-3 text-[11px] sm:text-base md:text-lg lg:text-[19px] text-white/95 font-sans font-normal max-w-3xl mx-auto drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center !text-center">
                {bannerSubtitle}
              </p>
            )}

            {/* Breadcrumb Navigation */}
            <div className="mt-2 sm:mt-3.5 md:mt-5 flex items-center justify-center gap-1.5 text-[10px] sm:text-[13px] uppercase tracking-wider text-white/80 font-medium select-none">
              <Link to="/" className="hover:text-white transition-colors">
                HOME
              </Link>
              <span className="text-white/60 mx-1">&rsaquo;</span>
              <span className="font-bold text-white tracking-widest">
                {bannerBreadcrumb}
              </span>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ======================================================== */}
      {/* SECTION 1: OUR INFRASTRUCTURE */}
      {/* ======================================================== */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto w-full lg:w-[75%] lg:max-w-[75%] px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 w-full">
              <Reveal>
                <div className="mb-6">
                  <span className="text-[13px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                    {infraKicker}
                  </span>
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {infraHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-5" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-4 text-gray-600 leading-relaxed text-[17px]">
                  {renderParagraphs(infra.paragraphs, DEFAULT_INFRA_PARAS)}
                </div>

                {/* Infrastructure Badges / Chips */}
                {infraBadges.length > 0 && (
                  <div className="mt-7 flex flex-wrap gap-2.5">
                    {infraBadges.map((badge, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs md:text-sm font-semibold bg-[#f9f8f4] border border-gray-200 text-[#1b3160] shadow-sm hover:border-[#d8a85f] transition-colors"
                      >
                        <i className="fas fa-check-circle text-[#d8a85f] text-xs" />
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>

            {/* Right Image */}
            <Reveal delayMs={70} className="order-1 lg:order-2">
              <div className="relative p-4 border border-gray-200 bg-[#f9f8f4] shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(infra.image, '/images/page-banners/why-choose-us.jpg')}
                    alt={infraHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/aboutus.jpeg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-20 h-20 md:w-24 md:h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-school text-2xl md:text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ======================================================== */}
      {/* SECTION 2: HOLISTIC APPROACH */}
      {/* ======================================================== */}
      <section className="bg-[#f9f8f4] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Image */}
            <Reveal delayMs={70} className="order-1">
              <div className="relative p-4 border border-gray-200 bg-white shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(holistic.image, '/images/library.JPG')}
                    alt={holisticHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/Computer-lab-scaled.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-20 h-20 md:w-24 md:h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-shapes text-2xl md:text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>

            {/* Right Content */}
            <div className="order-2 w-full">
              <Reveal>
                <div className="mb-6">
                  <span className="text-[13px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                    {holisticKicker}
                  </span>
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {holisticHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-5" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-4 text-gray-600 leading-relaxed text-[17px]">
                  {renderParagraphs(holistic.paragraphs, DEFAULT_HOLISTIC_PARAS)}
                </div>

                {/* Holistic Highlights Grid */}
                {holisticHighlights.length > 0 && (
                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {holisticHighlights.map((hl, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-gray-200/80 text-sm font-semibold text-[#1b3160]"
                      >
                        <i className="fas fa-star text-[#d8a85f] text-xs" />
                        <span>{hl}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================== */}
      {/* SECTION 3: STUDENT CARE & EMOTIONAL WELL-BEING */}
      {/* ======================================================== */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 w-full">
              <Reveal>
                <div className="mb-6">
                  <span className="text-[13px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                    {careKicker}
                  </span>
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {careHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-5" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-4 text-gray-600 leading-relaxed text-[17px]">
                  {renderParagraphs(care.paragraphs, DEFAULT_CARE_PARAS)}
                </div>

                {/* Callout Quote Box */}
                {careQuote && (
                  <div className="mt-7 p-6 rounded-2xl bg-[#f9f8f4] border-l-4 border-[#d8a85f] shadow-sm">
                    <p className="italic font-serif font-medium text-lg md:text-xl text-[#1b3160] leading-snug">
                      “{careQuote}”
                    </p>
                    <span className="text-xs uppercase tracking-wider font-bold text-[#d8a85f] mt-2 block">
                      Core Commitment to Every Child
                    </span>
                  </div>
                )}
              </Reveal>
            </div>

            {/* Right Image */}
            <Reveal delayMs={70} className="order-1 lg:order-2">
              <div className="relative p-4 border border-gray-200 bg-[#f9f8f4] shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(care.image, '/images/Computer-lab-scaled.jpg')}
                    alt={careHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/page-banners/why-choose-us.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-20 h-20 md:w-24 md:h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-heart text-2xl md:text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ======================================================== */}
      {/* SECTION 4: FOUR CORE PILLARS OF EXCELLENCE */}
      {/* ======================================================== */}
      <section className="bg-[#f9f8f4] py-16 md:py-24 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-[13px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                Foundations of Growth
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1b3160] leading-tight">
                {pillarsHeading}
              </h2>
              {pillarsSubtitle && (
                <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
                  {pillarsSubtitle}
                </p>
              )}
              <div className="w-16 h-1 bg-[#d8a85f] mx-auto mt-6" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillarItems.map((pillar, idx) => (
              <Reveal key={idx} delayMs={idx * 80}>
                <div className="h-full bg-white border border-gray-200/80 hover:border-[#d8a85f]/60 hover:shadow-xl transition-all duration-300 rounded-2xl p-6 flex flex-col group hover:-translate-y-1 shadow-sm">
                  <div className="w-14 h-14 rounded-2xl bg-[#1b3160]/5 text-[#d8a85f] group-hover:bg-[#1b3160] group-hover:text-[#d8a85f] flex items-center justify-center text-2xl mb-5 transition-colors shadow-inner">
                    <i className={`fas ${pillar.icon || 'fa-award'}`} />
                  </div>
                  <h4 className="text-lg font-bold text-[#1b3160] mb-2.5">
                    {pillar.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed flex-grow">
                    {pillar.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
